import { Link } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, MapPin, Award } from "lucide-react";
import SEO from "../components/SEO";
import ReviewsSection from "../components/ReviewsSection";
import ContactCTA from "../components/ContactCTA";
import ReferensGallery from "../components/ReferensGallery";
import {
  useGoogleReviews,
  formatRatingSv,
  formatCountSv,
} from "../lib/googleReviews";

/**
 * Samtliga 10 riktiga arbetsbilder från Trädfällarna Laholm.
 * Ordningen är vald så att galleriet öppnar på den bredaste hero-motivbilden
 * och sedan varierar mellan klättring, kranbil, team, städning och stubbfräsning.
 */
const WORK_IMAGES = [
  {
    src: "/images/referenser/tradfallarna-jobb-05-kranbil-stockar.jpg",
    alt: "Kranbil lyfter stockar efter trädfällning i Laholm",
    caption: "Kranbil med stockar",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-06-arborist-stam.jpg",
    alt: "Arborist från Trädfällarna klättrar i trädstam med sågutrustning",
    caption: "Klättring och sektionsfällning",
    objectPosition: "50% 30%",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-07-kranbil-tradfallning.jpg",
    alt: "Maskinell trädfällning med kranbil på tomt i Laholmsområdet",
    caption: "Maskinell trädfällning",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-04-klattrare-kran.jpg",
    alt: "Klättrare och kranbil i samverkan vid trädfällning nära bostad",
    caption: "Klättring i samspel med kran",
    objectPosition: "50% 35%",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-09-kranbil-klattrare.jpg",
    alt: "Kranbil och klättrare i arbete med större träd",
    caption: "Kranbil och arborist",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-10-team-stor-stubbe.jpg",
    alt: "Team från Trädfällarna vid en stor kapad stam i fältmiljö",
    caption: "Team och stor stubbe",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-08-stubbe-motorsag.jpg",
    alt: "Stubbe kapad med motorsåg redo för stubbfräsning",
    caption: "Stubbe redo för fräsning",
    objectPosition: "50% 45%",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-01-beskarning-stangverktyg.jpg",
    alt: "Beskärning på höjd med stångsåg",
    caption: "Beskärning med stångsåg",
    objectPosition: "50% 30%",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-02-arbetsklader-logga.jpg",
    alt: "Arbetskläder med Trädfällarnas logga",
    caption: "På plats i arbete",
  },
  {
    src: "/images/referenser/tradfallarna-jobb-03-stadning-lovblas.jpg",
    alt: "Städning med lövblås efter avslutat trädarbete",
    caption: "Städning efter jobbet",
    objectPosition: "50% 55%",
  },
];

const CLIENTS = [
  {
    name: "Terranor AB",
    src: "/images/logotyper/terranor-ab.png",
    href: "https://terranor.se/",
  },
  {
    name: "LBC Ängstorp Laholm",
    src: "/images/logotyper/lbc-angstorp-laholm.png",
    href: "https://lbc-angstorp.se/",
  },
  {
    name: "Laholms kommun",
    src: "/images/logotyper/laholms-kommun.png",
    href: "https://laholm.se/",
  },
  {
    name: "Länsstyrelsen Halland",
    src: "/images/logotyper/lansstyrelsen-hallands-lan.png",
    href: "https://www.lansstyrelsen.se/halland.html",
  },
];

const TRUST = [
  { icon: ShieldCheck, label: "Utbildade och försäkrade" },
  { icon: Award, label: "Tydlig offert med RUT" },
  { icon: MapPin, label: "Lokalt i Laholm med omnejd" },
];

export default function Referenser() {
  const google = useGoogleReviews();
  return (
    <div data-testid="referenser-page">
      <SEO
        title="Referenser | Trädfällarna Laholm"
        description="Se utförda arbeten, kundomdömen och exempel på verksamheter som anlitat Trädfällarna i Laholm. Över 1 000 kunder har anlitat oss för trädarbete."
        path="/referenser"
        image="https://trädfällarna.nu/images/referenser/tradfallarna-jobb-05-kranbil-stockar.jpg"
      />

      {/* HERO / social proof --------------------------------------------- */}
      <section
        data-testid="referenser-hero"
        className="relative min-h-[520px] md:min-h-[580px] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="/images/referenser/tradfallarna-jobb-05-kranbil-stockar.jpg"
            alt="Trädfällarna Laholm lyfter stockar med kranbil efter utfört arbete"
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 tf-hero-overlay" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-white">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-5">
              <span className="w-2 h-2 rounded-full bg-[#D95D2E]" />
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/90">
                Referenser
              </span>
            </div>
            <h1
              data-testid="referenser-hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.05] tracking-tight tf-hero-text-shadow"
            >
              Över{" "}
              <span className="text-[#F2A57C]">1&nbsp;000&nbsp;kunder</span>{" "}
              har anlitat Trädfällarna i Laholm
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/90 max-w-2xl leading-relaxed">
              Från villaträdgårdar och svåra trädfällningar till större uppdrag
              för företag, kommuner och myndigheter. Vi arbetar tryggt,
              effektivt och lämnar platsen väl omhändertagen efter oss.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/kontakt#offert"
                data-testid="referenser-hero-cta"
                className="inline-flex items-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold text-base px-5 py-3 rounded-lg transition-colors"
              >
                Få offert
                <ArrowRight className="w-4 h-4" />
              </Link>
              {google.hasData && (
                <Link
                  to="/#recensioner"
                  data-testid="referenser-hero-rating"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/25 text-white font-semibold text-sm px-4 py-3 rounded-lg transition-colors"
                >
                  <span className="flex items-center gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star
                        key={i}
                        className="w-3.5 h-3.5 fill-[#F2A57C] text-[#F2A57C]"
                        strokeWidth={1.5}
                      />
                    ))}
                  </span>
                  {formatRatingSv(google.rating)}/5 ·{" "}
                  {formatCountSv(google.ratingCount)} omdömen på Google
                </Link>
              )}
            </div>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/85">
              {TRUST.map((t) => (
                <li
                  key={t.label}
                  className="inline-flex items-center gap-2"
                  data-testid="referenser-trust-item"
                >
                  <t.icon className="w-4 h-4 text-[#F2A57C]" />
                  {t.label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* B — Utförda arbeten (galleri) ---------------------------------- */}
      <section
        data-testid="referenser-gallery"
        className="section-pad bg-[#F7F5F0]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 lg:mb-14">
            <span className="eyebrow">Utförda arbeten</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1B2A1E] leading-tight tracking-tight">
              Ett urval av våra arbeten
            </h2>
            <p className="mt-5 text-[#3a4338] text-lg leading-relaxed">
              Några exempel från arbeten vi utfört i Laholm med omnejd — från
              avancerad sektionsfällning och klättring till maskinell
              trädfällning och bortforsling.
            </p>
          </div>

          <ReferensGallery images={WORK_IMAGES} />
        </div>
      </section>

      {/* C — Uppdragsgivare -------------------------------------------- */}
      <section
        data-testid="referenser-clients"
        className="section-pad bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">Uppdragsgivare</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1B2A1E] leading-tight tracking-tight">
              Anlitade av både privatpersoner och större verksamheter
            </h2>
            <p className="mt-5 text-[#3a4338] text-lg leading-relaxed">
              Utöver våra många privatkunder har vi fått förtroendet att utföra
              arbeten för bland andra:
            </p>
          </div>

          <ul
            data-testid="referenser-clients-grid"
            className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6"
          >
            {CLIENTS.map((c) => (
              <li
                key={c.name}
                data-testid={`referenser-client-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <a
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Besök ${c.name} (öppnas i ny flik)`}
                  className="group flex items-center justify-center w-full h-[140px] sm:h-[150px] lg:h-[160px] rounded-xl border border-[#E2E0D8] hover:border-[#D95D2E] bg-white transition-colors px-4 sm:px-5"
                >
                  <img
                    src={c.src}
                    alt={`${c.name} — uppdragsgivare`}
                    className="max-h-[70%] max-w-full w-auto object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* D — Google-omdömen (återanvänder samma live-data) --------------- */}
      <ReviewsSection
        heading="Vad våra kunder säger"
        subheading="Aktuella 5-stjärniga omdömen hämtade direkt från Google."
      />

      {/* E — Avslutande CTA -------------------------------------------- */}
      <ContactCTA text="Beskriv vad du behöver hjälp med och skicka gärna med en bild på trädet. Då kan vi snabbt få en bättre bild av jobbet och återkomma med nästa steg." />
    </div>
  );
}
